'use client'

import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from 'ui/components/button'
import { BsGoogle } from 'react-icons/bs'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from 'ui/components/accordion'
import { useGoogle } from '../hooks/useGoogle'
import { redirectToGoogleOAuth, OAuthPlatform } from '../utils/google-oauth'

export interface GoogleBtnSCNProps {
  variant?: 'default' | 'outline'
  onAccept?: (access_token: string) => void
  isLoading?: boolean
  setSignMethod: (method: 'google' | 'credentials') => void
  hideMoreOptions?: boolean
  platform: OAuthPlatform
  apiBaseUrl: string
  /** Forwarded to the redirect flow so the login returns the user there. */
  next?: string | null
}

export const GoogleBtnSCN = ({
  variant = 'default',
  platform,
  apiBaseUrl,
  next,
  ...props
}: GoogleBtnSCNProps) => {
  const usesPopupFlow = !!props.onAccept
  const [isRedirecting, setIsRedirecting] = useState(false)

  const { initGoogleClient, googleClient } = useGoogle(
    props.onAccept ?? (() => {}),
  )

  useEffect(() => {
    if (!usesPopupFlow) return

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => initGoogleClient()
    document.body.appendChild(script)

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [initGoogleClient, usesPopupFlow])

  const handleClick = useCallback(async () => {
    if (usesPopupFlow && googleClient) {
      googleClient.requestAccessToken()
      return
    }

    setIsRedirecting(true)
    try {
      await redirectToGoogleOAuth({ platform, apiBaseUrl, next })
    } catch (error) {
      console.error('Failed to init OAuth:', error)
      setIsRedirecting(false)
    }
  }, [usesPopupFlow, googleClient, platform, apiBaseUrl, next])

  const isButtonLoading = props.isLoading || isRedirecting

  return (
    <div className='flex flex-col gap-2'>
      <Button
        size='lg'
        variant={variant}
        className='w-full'
        onClick={handleClick}
        disabled={isButtonLoading}
        isLoading={isButtonLoading}
        textCenter
      >
        <BsGoogle />
        <span className='text-base'>Continuar con Google</span>
      </Button>

      {!props.hideMoreOptions && (
        <Accordion className='border-0' type='single' collapsible>
          <AccordionItem className='border-0' value='item-1'>
            <AccordionTrigger
              disabled={props.isLoading}
              className='no-underline flex flex-row items-center justify-center w-78 py-2 focus:no-underline hover:underline'
            >
              <span className='me-1 text-foreground text-sm font-semibold pointer-events-auto'>
                Más opciones
              </span>
            </AccordionTrigger>

            <AccordionContent className='pt-2'>
              <div className='relative'>
                <Button
                  textCenter
                  onClick={() => props.setSignMethod('credentials')}
                  style={{
                    width: '100%',
                    height: '44px',
                    border: 'none',
                    borderRadius: '0.625rem',
                  }}
                  disabled={props.isLoading}
                  variant='secondary'
                >
                  Continuar con email
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <p className='text-xs text-gray-300'>
        Al hacer clic en Continuar, reconoce que ha leído y acepta los Términos
        de servicio y la Política de privacidad de Kach.
      </p>
    </div>
  )
}
